// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import { Link } from 'react-router-dom';
import { countByStatus } from '../../services/sopGovernanceSelectors';
import type { SopRegisterRow } from '../../types/sopGovernance';
import { equipmentTypeLabel, formatStatus, formatVersion, stripOntologyPrefix } from './formatters';

/**
 * Upstream statuses, in the order their tiles appear. This is the SOP Factory
 * `Status` enum verbatim — not the design binding's Current / Outdated / Draft /
 * Orphaned, which is a different vocabulary the catalogue does not serve. See
 * the PR description for the mapping and what's missing from it.
 */
const STATUSES = ['approved', 'in_review', 'draft', 'deprecated'] as const;

/** The register's four count tiles, one per upstream status. */
export function SopRegisterTiles({ rows }: { rows: SopRegisterRow[] }) {
  const counts = countByStatus(rows);
  return (
    <div className="sg-summary sg-summary--register">
      {STATUSES.map(status => (
        <article
          key={status}
          className={`sg-tile sg-tile--${status.replace('_', '-')}`}
          data-testid={`sg-tile-${status}`}
        >
          <strong>{counts[status] ?? 0}</strong>
          <span className="sg-label">{formatStatus(status)}</span>
        </article>
      ))}
    </div>
  );
}

/** The fleet SOP register. */
export default function SopRegisterTable({ rows }: { rows: SopRegisterRow[] }) {
  return (
    <div className="sg-panel">
      <table className="sg-table sg-table--register">
        <thead>
          <tr>
            <th scope="col">SOP</th>
            <th scope="col">Procedure</th>
            <th scope="col">Status</th>
            <th scope="col">Revision</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={row.artifactId} data-testid="sop-register-row" data-sop-id={row.artifactId}>
              <td className="sg-mono">
                {/* The SOP detail screen already resolves an artifact by id. */}
                <Link to={`/sops/${encodeURIComponent(row.artifactId)}`} title={row.artifactId}>
                  {row.artifactId}
                </Link>
              </td>
              <td>
                <div className="sg-site-name">{row.title}</div>
                {/* The binding's note is prose about the binding ("Bound to ATS
                    assets"), which no contract serves. The equipment type is
                    what upstream does say — rendered as a label rather than the
                    raw `wattsch:ChilledWaterSystem`. */}
                {row.equipmentType && (
                  <div className="sg-sop-note">
                    {equipmentTypeLabel(stripOntologyPrefix(row.equipmentType))}
                  </div>
                )}
              </td>
              <td>
                <span
                  className={`sg-state sg-state--${(row.status || 'unknown').replace('_', '-')}`}
                  data-testid="sop-register-status"
                >
                  {formatStatus(row.status)}
                </span>
              </td>
              <td className="sg-mono sg-rev">{formatVersion(row.version)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
